import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import HomePage from '@/pages/HomePage';
import { ConfigurableHero } from '@/components/ConfigurableHero';

/**
 * PreviewHomePage
 * ---------------
 * Renders the actual HomePage using a chosen homepage blueprint (?src=draft or
 * ?src=live). The visitor sees exactly what the live homepage will look like
 * once the current draft is published.
 *
 * Implementation note: We reuse HomePage as-is (everything below the hero
 * shares the same public endpoints), and override the ChannelHero region by
 * rendering ConfigurableHero with the chosen blueprint above it. To keep
 * things simple we hide HomePage's own hero by mounting a small style
 * override that scopes the wrapper.
 *
 * Access: this route is intentionally admin-preview friendly but does not
 * expose any admin-only endpoints beyond /api/admin/homepage (which requires
 * bearer auth). Public preview relies on /api/homepage/featured?src=draft.
 */
export default function PreviewHomePage() {
  const [params] = useSearchParams();
  const src = params.get('src') === 'draft' ? 'draft' : 'live';
  const [blueprint, setBlueprint] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Try admin endpoint first (authenticated admins get the draft).
        // Falls back to /homepage (live) if auth fails or src=live.
        if (src === 'draft') {
          try {
            const { data } = await api.get('/admin/homepage');
            setBlueprint(data.draft || null);
            return;
          } catch (_) {
            // Fallback to live if the admin isn't logged in.
          }
        }
        const { data } = await api.get('/homepage');
        setBlueprint(data || null);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Preview unavailable');
      }
    })();
  }, [src]);

  return (
    <div data-testid="preview-home-page">
      <PreviewBanner src={src} />
      {blueprint ? (
        <HomePage heroOverride={<ConfigurableHero blueprint={blueprint} source={src} />} />
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--pg-ink-muted)]">Loading preview…</div>
      )}
      {error && <div className="max-w-7xl mx-auto px-6 py-4 text-xs text-[var(--pg-neon-pink)] font-mono">{error}</div>}
    </div>
  );
}

function PreviewBanner({ src }) {
  return (
    <div className="sticky top-0 z-50 bg-[var(--pg-navy-900)] border-b border-[var(--pg-gold-2)] text-[var(--pg-ink)] px-4 py-2 flex items-center justify-between" data-testid="preview-banner">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-widest text-[var(--pg-gold-2)] uppercase">Homepage Preview</span>
        <span className="text-[10px] font-mono text-[var(--pg-ink-faint)]">Source: {src.toUpperCase()}</span>
      </div>
      <a href="/admin?tab=experience" className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] uppercase">Back to Experience Manager →</a>
    </div>
  );
}
